import { Component, inject, OnInit, ChangeDetectorRef, viewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { ManageUser, ManageUserResult } from '@shared/model/auth.models';
import { Classroom, Grade } from '@shared/model/classroom.models';
import { Subject } from '@shared/model/course.models';
import { AuthService } from '@shared/service/auth.service';
import { ClassroomService } from '@shared/service/classroom.service';
import { CourseService } from '@shared/service/course.service';
import { SharedService } from '@shared/service/shared.service';
import { regularSubjectList } from '@shared/utils/utils';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzTableComponent } from 'ng-zorro-antd/table';
import { Site, SiteService } from '@shared/service/site.service';
import { changeRouteQuery, parseRouterParams } from '@shared/utils/router-utils';
import { isNil } from 'es-toolkit';

@Component({
  selector: 'fl-user-manage',
  templateUrl: './user-manage.component.html',
  styleUrls: ['./user-manage.component.scss'],
  standalone: false,
})
export class UserManageComponent implements OnInit {
  readonly editUserTemplRef = viewChild<any>('edit');
  readonly nzTable = viewChild<NzTableComponent<ManageUser>>('nzTable');
  list: ManageUser[] = [];

  gradeList: Grade[] = [];
  total: number;

  page = 1;

  editUserForm: UntypedFormGroup;
  // since 21.2.3 查看学生信息
  viewUserForm: UntypedFormGroup;

  // 学生编辑框设置
  stdEditSetting = {
    show: false,
    loading: false,
  };

  loading = false;

  currentUser: ManageUser;

  // 当前激活的tab面板
  currentTab = 0;

  // table当前是否加载中
  isTableLoading = false;

  /**
   * 选中的站点ID
   */
  selectedSiteId: number | null = null;

  /**
   * 站点列表
   */
  siteList: Site[] = [];

  /**
   * 年级
   */
  grade: number;
  /**
   * 班级
   */
  classroom: number;
  /**
   * 学科
   */
  subject: number;
  /**
   * 角色
   */
  role: number;
  /**
   * 账号启用状态：true 启用 / false 禁用 / null 全部
   */
  isActive: boolean | null = null;
  /**
   * 关键词
   */
  kw: string;

  // 搜索条件
  searchOptions = {
    grade: null,
    classroom: null,
    subject: null,
    role: null,
    kw: null,
  };
  // 学科列表
  subjectList: Array<Subject>;
  classroomList: Array<Classroom>;

  checked: boolean = false;
  indeterminate: boolean = false;
  selectedIds: Set<number> = new Set<number>();

  get currentPageData(): ManageUser[] {
    return this.list || [];
  }

  get isStaffSite(): boolean {
    return this.sharedSrv.site.editCourse && this.sharedSrv.user.isOperateInSchool;
  }

  private authSrv: AuthService = inject(AuthService);
  private router: Router = inject(Router);
  private route: ActivatedRoute = inject(ActivatedRoute);
  private fb: UntypedFormBuilder = inject(UntypedFormBuilder);
  private courseSrv: CourseService = inject(CourseService);
  public sharedSrv: SharedService = inject(SharedService);
  private classroomSrv: ClassroomService = inject(ClassroomService);
  private title: Title = inject(Title);
  private msgSrv: NzMessageService = inject(NzMessageService);
  private siteSrv: SiteService = inject(SiteService);
  private cdr: ChangeDetectorRef = inject(ChangeDetectorRef);
  constructor() {
    this.title.setTitle('用户管理' + ' - ' + this.sharedSrv.site.siteTitle);
  }

  async ngOnInit() {
    // 从查询参数中获取搜索条件和tab状态
    const queryParams = parseRouterParams(this.route.snapshot.queryParams);
    if (queryParams['siteId']) {
      this.selectedSiteId = Number(queryParams['siteId']);
    }
    if (queryParams['grade']) {
      this.grade = Number(queryParams['grade']);
    }
    if (queryParams['classroom']) {
      this.classroom = Number(queryParams['classroom']);
    }
    if (queryParams['subject']) {
      this.subject = Number(queryParams['subject']);
    }
    if (queryParams['role']) {
      this.role = Number(queryParams['role']);
    }
    if (queryParams['kw']) {
      this.kw = queryParams['kw'];
    }
    if ('isActive' in queryParams) {
      this.isActive = queryParams['isActive'] === 'false' ? false : Boolean(queryParams['isActive']);
    }
    if (queryParams['page']) {
      this.page = Number(queryParams['page']);
    }
    if (queryParams['tab']) {
      this.currentTab = Number(queryParams['tab']);
    }

    if (this.isStaffSite) {
      await this.getMyAllSites();
    } else {
      await this.loadGrades();
      this.search();
    }
    this.list = [];

    // 如果有搜索参数，执行搜索
    if (this.selectedSiteId || this.grade || this.classroom || this.subject || this.role || this.kw) {
      await this.search(this.page);
    }

    // 修改信息
    this.editUserForm = this.fb.group({
      firstName: ['', [Validators.required, Validators.pattern('^[\\u4e00-\\u9fa5]{2,6}$')]],
      isActive: [true],
      questionAssociateNode: [false],
      role: [[]],
      subject: [null],
    });

    this.viewUserForm = this.fb.group({
      firstName: ['', [Validators.required]],
      isActive: [true],
      studentNo: ['', [Validators.pattern('^(\\d{4,12})?$')]],
    });
  }

  async onEdit(user: ManageUser) {
    // 然后导航到编辑页面
    await this.router.navigate([`/auth/users/edit/${user.id}`], {
      queryParams: {
        siteId: this.selectedSiteId,
      },
    });
  }

  // 学生账号查看操作
  onView(user: ManageUser): void {
    this.authSrv.getStudentInfo(user.id).then(student => {
      if (student.classroom && student.classroom.length > 0) {
        student.classroom = (student.classroom as any).sort((a, b) => a.type - b.type);
      }
      this.currentUser = student;
      this.viewUserForm.reset({
        firstName: student.firstName,
        isActive: student.isActive,
        studentNo: student.studentNo,
      });
      this.stdEditSetting.show = true;
      // OnPush：then 续体非模板事件，需主动标记（查看弹层弹出依赖此渲染）
      this.cdr.markForCheck();
    });
  }

  async editStd() {
    this.stdEditSetting.loading = true;
    // 编辑用户信息
    for (const i in this.viewUserForm.controls) {
      if (this.viewUserForm.controls.hasOwnProperty(i)) {
        this.viewUserForm.controls[i].markAsDirty();
        this.viewUserForm.controls[i].markAsTouched();
      }
    }

    if (this.viewUserForm.invalid) {
      this.stdEditSetting.loading = false;
      return;
    }
    try {
      const data = await this.authSrv.updateStudentInfo(
        this.currentUser.id,
        this.viewUserForm.get('firstName').value,
        this.viewUserForm.get('isActive').value,
        this.viewUserForm.get('studentNo').value || null
      );
      await this.search(this.page);
    } catch (e) {
      this.stdEditSetting.loading = false;
      // OnPush：失败路径先标记再抛出，避免 loading 状态无法回显
      this.cdr.markForCheck();
      throw e;
    }

    this.msgSrv.success('修改成功.');
    this.stdEditSetting = { loading: false, show: false };
    // OnPush：await 续体关闭弹层，需主动标记
    this.cdr.markForCheck();
  }

  /**
   * 选项卡改变事件
   * index: 0.学生账号 1.教师账号
   */
  selectedTabChange(index: number): void {
    this.currentTab = index;
    this.resetSearchParams();
    this.list = [];
    this.page = 1;
    this.total = 0;

    if (!this.isStaffSite) this.search();
  }

  async onUnBind(user) {
    const { detail } = await this.authSrv.unBindUserDevice(user.id);
    this.msgSrv.info(detail);
  }

  async loadGrades() {
    this.gradeList = await this.classroomSrv.getGradeList({ siteId: this.selectedSiteId });
    // OnPush：异步赋值后需主动标记，否则视图不刷新
    this.cdr.markForCheck();
  }

  async loadClassrooms() {
    this.classroomList = await this.classroomSrv.getClassList(this.grade);
    this.cdr.markForCheck();
  }

  /**
   * 站点选择变化事件
   */
  async onSiteChange(siteId: number | null) {
    this.selectedSiteId = siteId;
    this.classroom = null;
    this.grade = null;
    await this.loadGrades();
    await this.search();
  }

  /**
   * 获取我所有的分站列表
   */
  async getMyAllSites() {
    const result = await this.siteSrv.getSites(true); // 获取所有站点

    this.siteList = result || [];
    this.cdr.markForCheck();

    if (this.selectedSiteId) {
      this.onSiteChange(this.selectedSiteId);
    }
  }

  /**
   * 年级选择变化事件
   * @param gradeId
   */
  async onSelectGrade(gradeId: number) {
    this.classroom = null;
    /**
     * 只有在学生账号管理的时候才需要请求这个接口。
     */
    if (!isNil(gradeId) && this.currentTab === 0) await this.loadClassrooms();
    await this.search();
  }

  /**
   * 班级选择变化事件
   * @param classroomId
   */
  async onSelectClassroom(classroomId: number) {
    this.classroom = classroomId;
    await this.search();
  }

  /**
   * 科目选择变化事件
   * @param subjectId
   */
  async onSelectSubject(subjectId: number) {
    this.subject = subjectId;
    await this.search();
  }

  /**
   * 角色选择变化事件
   * @param roleId
   */
  async onSelectRole(roleId: number) {
    this.role = roleId;
    await this.search();
  }

  /**
   * 账号状态选择变化事件
   * @param value true 启用 / false 禁用 / null 全部
   */
  async onSelectActive(value: boolean | null) {
    this.isActive = value ?? null;
    await this.search();
  }

  async search(page = 1) {
    this.page = page;
    if (this.isStaffSite && !this.selectedSiteId) return;
    this.loading = false;
    this.isTableLoading = true;
    const result = (await this.authSrv.getUsers({
      page: this.page,
      isStaff: Boolean(this.currentTab),
      grade: this.grade,
      classroom: this.classroom,
      subject: this.subject,
      role: this.role,
      kw: this.kw,
      siteId: this.selectedSiteId,
      isActive: this.isActive,
    })) as ManageUserResult;

    this.courseSrv.getSubjectList().then(result => {
      const grade = this.gradeList.filter(_grade => _grade.id === this.grade);
      if (grade && grade.length > 0 && grade[0].studyGrade) {
        this.subjectList = regularSubjectList(result).filter(subject => subject.grade === grade[0].studyGrade);
      } else {
        this.subjectList = regularSubjectList(result);
      }
      // OnPush：then 续体晚于 search 末尾的标记执行，需单独标记
      this.cdr.markForCheck();
    });
    this.isTableLoading = false;
    this.list = (result.results || []).map(user => ({
      ...user,
      checked: !!(user.id && this.selectedIds.has(user.id)),
    }));
    this.total = result.count;
    this.loading = true;
    this.changeRouter();
    this.checkChecked();
    // OnPush：异步赋值后需主动标记，否则视图不刷新
    this.cdr.markForCheck();
  }

  resetSearchParams() {
    this.selectedSiteId = null;
    this.grade = null;
    this.classroom = null;
    this.subject = null;
    this.role = null;
    this.kw = '';
    this.isActive = null;
  }

  onCheckAll() {
    const currentPageDataIds = this.currentPageData.map(i => i.id);
    this.list.forEach(i => {
      if (!i.id) return;
      if (currentPageDataIds.includes(i.id)) {
        i.checked = this.checked;
        if (this.checked) {
          this.selectedIds.add(i.id);
        } else {
          this.selectedIds.delete(i.id);
        }
      }
    });
    this.checkChecked();
  }

  onItemChecked(user: ManageUser) {
    if (!user.id) return;
    if (user.checked) {
      this.selectedIds.add(user.id);
    } else {
      this.selectedIds.delete(user.id);
    }
    this.checkChecked();
  }

  checkChecked() {
    this.checked = this.currentPageData.length && this.currentPageData.every(i => this.selectedIds.has(i.id));
    this.indeterminate = this.currentPageData.some(i => this.selectedIds.has(i.id)) && !this.checked;
  }

  changeRouter() {
    changeRouteQuery(this.router, {
      page: this.page,
      grade: this.grade,
      subject: this.subject,
      role: this.role,
      siteId: this.selectedSiteId,
      kw: this.kw,
      tab: this.currentTab,
      classroom: this.classroom,
      isActive: this.isActive,
    });
  }
}
